import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import { INotification } from '../models';
import { NotificationType } from '../enums';
import { uuid } from '../functions';


@Injectable({
	providedIn: 'root'
})
export class NotificationService {
	private notifications = new BehaviorSubject<INotification[]>([] as INotification[]);
	readonly notifications$: Observable<INotification[]> = this.notifications.asObservable();

	/**
	 * Adds a notification to the list and optionally auto-closes it after 3 seconds.
	 * @param {INotification} notification - The notification object to add.
	 */
	notify(notification: INotification) {
		// Copy of the notifications array to modify it
		const notifications = this.getNotifications().slice(0);

		// In case it is null or undefined
		if (notification.autoClose !== false) {
			notification.autoClose = true;
		}
		if (!notification.id) {
			notification.id = uuid();
		}
		notifications.push(notification);
		this.notifications.next(notifications);

		if (notification.autoClose) {
			window.setTimeout(() => this.removeById(notification.id), notification.options?.autoCloseDelay || 3000);
		}
	}
	
	/**
	 * Adds a success notification to the list.
	 * @param {INotification} notification - The notification object to add.
	 */
	success(notification: INotification) {
		this.notify({ ...notification, type: NotificationType.SUCCESS });
	}

	/**
	 * Adds a success notification with a NotificationMessages.Success.*** message key.
	 * @param {string} key - The message key to display.
	 * @param {boolean} [autoClose=true] - Whether the notification should auto-close.
	 */
	successMessage(key: string, autoClose = true) {
		this.success({ message: 'NotificationMessages.Success.' + key, autoClose });
	}

	/**
	 * Adds an error notification to the list.
	 * @param {INotification} notification - The notification object to add.
	 */
	error(notification: INotification) {
		this.notify({ ...notification, type: NotificationType.ERROR });
	}

	/**
	 * Adds an error notification with a NotificationMessages.Errors.*** message key.
	 * @param {string} key - The message key to display.
	 * @param {boolean} [autoClose=true] - Whether the notification should auto-close.
	 */
	errorMessage(key: string, autoClose = true) {
		this.error({ message: 'NotificationMessages.Errors.' + key, autoClose });
	}

	/**
	 * Adds a warning notification to the list.
	 * @param {INotification} notification - The notification object to add.
	 */
	warning(notification: INotification) {
		this.notify({ ...notification, type: NotificationType.WARNING });
	}

	/**
	 * Adds a warning notification with a NotificationMessages.Warnings.*** message key.
	 * @param {string} key - The message key to display.
	 * @param {boolean} [autoClose=true] - Whether the notification should auto-close.
	 */
	warningMessage(key: string, autoClose = true) {
		this.warning({ message: 'NotificationMessages.Warnings.' + key, autoClose });
	}

	/**
	 * Returns the current list of notifications.
	 * @returns {INotification[]} The array of notifications.
	 */
	getNotifications(): INotification[] {
		return this.notifications.value;
	}

	/**
	 * Removes a notification by its ID.
	 * @param {string} id - The ID of the notification to remove.
	 */
	removeById(id: string) {
		this.notifications.next(this.getNotifications().filter(n => n.id !== id));
	}

	/**
	 * Removes a notification by its index in the list.
	 * @param {number} index - The index of the notification to remove.
	 */
	remove(index: number) {
		const notifications = this.getNotifications().slice(0);
		notifications.splice(index, 1);

		this.notifications.next(notifications);
	}

	/**
	 * Clears all notifications.
	 */
	reset() {
		this.notifications.next([]);
	}
}
